import { useState, useCallback } from 'react';
import { API_CONFIG, getNodeApiHeaders } from '../helpers/constants';
import { OAuthConnectionData } from '../helpers/oauthConfig';
import { refreshAccessToken } from '../services/authService';

export interface UseUnifiedOAuthProps {
  channelId: string;
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
  /** Called when the Node server still returns 401 after a refresh attempt — triggers the app's session-expired flow. */
  onSessionExpired?: () => void;
}

export interface OAuthState {
  isConnecting: boolean;
  error: string | null;
}

export interface AuthUrlResponse {
  authUrl: string;
  state: string;
  codeVerifier?: string; // For PKCE
}

export interface OAuthTokenResponse {
  expires_in?: number;
  token_type: string;
  scope?: string;
  provider: string;
  connection_settings: {
    client_id: string;
    client_secret: string;
    provider: string;
    auth_url: string;
    token_url: string;
    access_token: string;
    refresh_token?: string;
    expires_in?: number;
    token_type: string;
    scope?: string;
    [key: string]: any; // For custom params like instance, domain, etc.
  };
}

export const useUnifiedOAuth = ({ channelId, onSuccess, onError, onSessionExpired }: UseUnifiedOAuthProps) => {
  const [state, setState] = useState<OAuthState>({
    isConnecting: false,
    error: null
  });

  const setConnecting = useCallback((connecting: boolean) => {
    setState(prev => ({ ...prev, isConnecting: connecting }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error }));
  }, []);

  const generateAuthUrl = useCallback(async (
    provider: string,
    clientId: string,
    customParams: Record<string, string>,
    appId: string
  ): Promise<AuthUrlResponse> => {
    const requestBody = JSON.stringify({
      provider,
      clientId,
      customParams,
      appId,
      channelId,
      redirectUri: `${window.location.origin}/auth/unified/callback`
    });

    const postToNode = (headers: Record<string, string>) =>
      fetch(`${API_CONFIG.NODE_BASE_URL}/rest/oauth/generate-auth-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: requestBody,
      });

    try {
      let response = await postToNode(getNodeApiHeaders());

      if (response.status === 401) {
        // The Node OAuth routes require a non-expired Shay access token, but most of
        // this app authenticates its own calls with the longer-lived refresh token —
        // so the access token can expire silently while the user still appears
        // logged in. Try one silent refresh + retry before giving up.
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          response = await postToNode({ Authorization: `Bearer ${newAccessToken}` });
        }
      }

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('SESSION_EXPIRED');
        }
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error generating OAuth URL:', error);
      throw error;
    }
  }, [channelId]);

  const exchangeToken = useCallback(async (
    provider: string,
    code: string,
    clientId: string,
    clientSecret: string,
    customParams: Record<string, string>,
    appId: string,
    codeVerifier?: string
  ): Promise<OAuthTokenResponse> => {
    try {
      const response = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/oauth/exchange-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getNodeApiHeaders(),
        },
        body: JSON.stringify({
          provider,
          code,
          redirectUri: `${window.location.origin}/auth/unified/callback`,
          clientId,
          clientSecret,
          customParams,
          appId,
          channelId,
          codeVerifier
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error exchanging OAuth token:', error);
      throw error;
    }
  }, [channelId]);

  const initiateOAuth = useCallback(async (
    connectionData: OAuthConnectionData,
    appId: string
  ): Promise<void> => {
    try {
      setConnecting(true);
      setError(null);

      // Generate OAuth authorization URL
      const authData = await generateAuthUrl(
        connectionData.provider,
        connectionData.clientId,
        connectionData.customParams,
        appId
      );

      // Store connection data temporarily for the callback
      const tempData = {
        provider: connectionData.provider,
        clientId: connectionData.clientId,
        clientSecret: connectionData.clientSecret,
        customParams: connectionData.customParams || {},
        appId,
        channelId,
        state: authData.state,
        codeVerifier: authData.codeVerifier // Store PKCE code verifier
      };
      
      const serializedData = JSON.stringify(tempData);
      
      // Store in localStorage (accessible across windows)
      localStorage.setItem('oauth_temp_data', serializedData);
      localStorage.setItem(`oauth_${authData.state}`, serializedData);
      
      console.log('🔄 Stored OAuth temp data in localStorage:', {
        key: 'oauth_temp_data',
        stateKey: `oauth_${authData.state}`,
        data: tempData,
        serializedLength: serializedData.length,
        serializedPreview: serializedData.substring(0, 200) + '...'
      });
      
      // Verify storage immediately
      const storedData = localStorage.getItem('oauth_temp_data');
      console.log('🔍 Verification - stored data retrieved from localStorage:', {
        success: !!storedData,
        matches: storedData === serializedData,
        length: storedData?.length
      });

      // Open OAuth popup
      const popup = window.open(
        authData.authUrl,
        'oauth_popup',
        'width=600,height=700,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        throw new Error('Failed to open OAuth popup. Please allow popups for this site.');
      }

      // Listen for OAuth completion
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
          return;
        }

        if (event.data.type === 'OAUTH_SUCCESS') {
          window.removeEventListener('message', handleMessage);
          setConnecting(false);
          
          if (onSuccess) {
            onSuccess(event.data.data);
          }
        } else if (event.data.type === 'OAUTH_ERROR') {
          window.removeEventListener('message', handleMessage);
          setConnecting(false);
          
          const errorMessage = event.data.error || 'OAuth authentication failed';
          setError(errorMessage);
          
          if (onError) {
            onError(errorMessage);
          }
        }
      };

      window.addEventListener('message', handleMessage);

      // Handle popup close without completion
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          
          // Only set error if we're still in connecting state (no success message received)
          setState(prev => {
            if (prev.isConnecting) {
              const errorMessage = 'OAuth authentication was cancelled';
              if (onError) {
                onError(errorMessage);
              }
              return {
                isConnecting: false,
                error: errorMessage
              };
            }
            return prev;
          });
        }
      }, 1000);

    } catch (error) {
      setConnecting(false);
      if (error instanceof Error && error.message === 'SESSION_EXPIRED') {
        if (onSessionExpired) {
          onSessionExpired();
        } else {
          // No session-expiry handler wired up by this caller — fall back to the
          // generic error path instead of failing silently (PR #598 review, N1).
          const sessionExpiredMessage = 'Your session has expired. Please log in again.';
          setError(sessionExpiredMessage);
          if (onError) {
            onError(sessionExpiredMessage);
          }
        }
        return;
      }
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(errorMessage);

      if (onError) {
        onError(errorMessage);
      }
    }
  }, [generateAuthUrl, onSuccess, onError, onSessionExpired, channelId]);

  const getProviders = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/oauth/providers`, {
        headers: { ...getNodeApiHeaders() },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.providers;
    } catch (error) {
      console.error('Error fetching OAuth providers:', error);
      throw error;
    }
  }, []);

  return {
    state,
    initiateOAuth,
    exchangeToken,
    getProviders,
    setError,
    setConnecting
  };
};

export default useUnifiedOAuth;
